import { useReducer, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import type { IEditCollectedDataDialogProps } from '@/@types/serviceTypes';
import type { IPolicyFieldValue } from '@/@types/insurance';

type TFormState = {
  // LOCAL_TYPE_JUSTIFIED: Reducer state type is internal to this dialog only
  values: Map<string, string>;
  isSaving: boolean;
};

type TFormAction = // LOCAL_TYPE_JUSTIFIED: Reducer action type is internal to this dialog only
  | { type: 'SET_VALUE'; label: string; value: string }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'RESET'; initialValues: Map<string, string> };

function formReducer(state: TFormState, action: TFormAction): TFormState {
  switch (action.type) {
    case 'SET_VALUE': {
      const next = new Map(state.values);
      next.set(action.label, action.value);
      return { ...state, values: next };
    }
    case 'SET_SAVING':
      return { ...state, isSaving: action.isSaving };
    case 'RESET':
      return { values: action.initialValues, isSaving: false };
  }
}

function buildInitialValues(
  serviceType: IEditCollectedDataDialogProps['serviceType'],
  serviceData: IEditCollectedDataDialogProps['serviceData']
): Map<string, string> {
  const map = new Map<string, string>();
  const collectedMap = new Map(serviceData.collectedFields.map(f => [f.fieldLabel, f.value ?? '']));

  for (const field of serviceType.requiredFields) {
    map.set(field.label, collectedMap.get(field.label) ?? '');
  }

  return map;
}

export function EditCollectedDataDialog({
  open,
  onOpenChange,
  serviceType,
  serviceData,
  onSave,
}: IEditCollectedDataDialogProps) {
  const [state, dispatch] = useReducer(formReducer, {
    values: buildInitialValues(serviceType, serviceData),
    isSaving: false,
  });

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        dispatch({ type: 'RESET', initialValues: buildInitialValues(serviceType, serviceData) });
      }
      onOpenChange(isOpen);
    },
    [onOpenChange, serviceType, serviceData]
  );

  const handleSave = useCallback(async () => {
    dispatch({ type: 'SET_SAVING', isSaving: true });

    const collectedFields: IPolicyFieldValue[] = serviceType.requiredFields.map(field => ({
      fieldLabel: field.label,
      value: state.values.get(field.label) || undefined,
    }));

    await onSave(collectedFields);
    dispatch({ type: 'SET_SAVING', isSaving: false });
    onOpenChange(false);
  }, [state.values, serviceType.requiredFields, onSave, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {serviceType.name} Data</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {serviceType.requiredFields.map(field => {
            const value = state.values.get(field.label) ?? '';

            if (field.fieldType === 'select' && field.options) {
              return (
                <div key={field.label} className="space-y-1.5">
                  <Label className="text-sm">{field.label}</Label>
                  <Select
                    value={value}
                    onValueChange={v =>
                      dispatch({ type: 'SET_VALUE', label: field.label, value: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            if (field.fieldType === 'boolean') {
              return (
                <div key={field.label} className="space-y-1.5">
                  <Label className="text-sm">{field.label}</Label>
                  <Select
                    value={value}
                    onValueChange={v =>
                      dispatch({ type: 'SET_VALUE', label: field.label, value: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            if (field.fieldType === 'file') {
              return (
                <div key={field.label} className="space-y-1.5">
                  <Label className="text-sm text-muted-foreground">{field.label}</Label>
                  <p className="text-xs text-muted-foreground">
                    File uploads are managed separately
                  </p>
                </div>
              );
            }

            return (
              <div key={field.label} className="space-y-1.5">
                <Label className="text-sm">{field.label}</Label>
                <Input
                  type={
                    field.fieldType === 'number'
                      ? 'number'
                      : field.fieldType === 'date'
                        ? 'date'
                        : 'text'
                  }
                  value={value}
                  onChange={e =>
                    dispatch({ type: 'SET_VALUE', label: field.label, value: e.target.value })
                  }
                  placeholder={field.description ?? `Enter ${field.label.toLowerCase()}`}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={state.isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={state.isSaving}>
            {state.isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
